import { LevelType } from '../types/Level';
import { buildSongResearchPrompt, parseSongResearch } from '../ai/songResearch';
import { AIError } from '../ai/types';
import { DraftLevel } from './types';
import {
  songQuery, songImagePrompt, scrubNames, isCurrentSongPrompt, sayingScenePrompt, sayingImagePrompt, realThingImagePrompt,
  INSPECTION_SCHEMA, buildInspectionPrompt, parseInspection,
} from './pictures';

export type PictureStep = 'researching' | 'designing' | 'drawing' | 'checking' | 'saving';

export interface PictureServices {
  // Claude with web search; returns the STORY/ELEMENTS text
  research: (prompt: string) => Promise<string>;
  // Plain Claude text
  text: (prompt: string) => Promise<string>;
  // The image engine
  draw: (prompt: string) => Promise<Blob>;
  // Claude looks at the picture and answers in the given JSON shape
  inspect: (image: Blob, prompt: string, schema: Record<string, unknown>) => Promise<unknown>;
  upload: (image: Blob, filename: string) => Promise<{ filename: string; size: number }>;
}

export interface PictureResult {
  imagePrompt: string;
  image: string;
  imageSize: number;
  flags: string[];
}

export const PICTURE_FLAG_PREFIX = 'Picture';

// Types whose picture is an illustration the AI can draw without being asked.
export const autoPicture = (type: LevelType): boolean =>
  type === LevelType.WhatsTheSong || type === LevelType.KnownSentence;

export const buildImagePrompt = async (
  draft: DraftLevel,
  services: PictureServices,
  onStep: (step: PictureStep) => void
): Promise<string> => {
  if (draft.levelType === LevelType.WhatsTheSong) {
    onStep('researching');
    const research = parseSongResearch(await services.research(buildSongResearchPrompt(songQuery(draft))));
    // Belt and braces: the research is told to leave these out, and they are removed here too
    const names = [draft.sentence || '', draft.context || ''];
    return songImagePrompt(scrubNames(research.story, names), scrubNames(research.elements, names));
  }
  if (draft.levelType === LevelType.KnownSentence) {
    onStep('designing');
    return sayingImagePrompt(await services.text(sayingScenePrompt(draft.sentence || '')));
  }
  return realThingImagePrompt(draft);
};

const describe = (err: unknown): string => (err instanceof Error ? err.message : String(err));

// A failure every remaining picture job would hit too: the image engine rejecting the request outright
// (bad key, billing, quota). A single odd reply or a song that cannot be found is not fatal.
export const isFatalPictureError = (err: unknown): boolean =>
  err instanceof AIError && err.provider === 'gemini' && !err.retryable;

// Writes the prompt (unless the draft already has one), draws, checks, redraws once if the check fails, and uploads.
export const makePicture = async (
  draft: DraftLevel,
  services: PictureServices,
  onStep: (step: PictureStep) => void = () => {},
  // Called once when a new prompt has been written, so it can be saved before anything else can fail
  onPrompt: (imagePrompt: string) => void = () => {}
): Promise<PictureResult> => {
  const isSong = draft.levelType === LevelType.WhatsTheSong;
  // A song prompt saved before the no-title, no-singer rules is written again rather than reused
  let imagePrompt = draft.imagePrompt && (!isSong || isCurrentSongPrompt(draft.imagePrompt)) ? draft.imagePrompt : '';
  if (!imagePrompt) {
    imagePrompt = await buildImagePrompt(draft, services, onStep);
    onPrompt(imagePrompt);
  }
  const inspectionPrompt = buildInspectionPrompt(imagePrompt);

  let image: Blob | null = null;
  let flags: string[] = [];

  for (let attempt = 0; attempt < 2; attempt++) {
    onStep('drawing');
    try {
      image = await services.draw(imagePrompt);
    } catch (err) {
      // No picture at all: fail. A failed redraw: keep the first picture, which is already paid for.
      if (!image) throw err;
      flags = [...flags, `${PICTURE_FLAG_PREFIX} redraw failed: ${describe(err)}`];
      break;
    }

    onStep('checking');
    try {
      const verdict = parseInspection(await services.inspect(image, inspectionPrompt, INSPECTION_SCHEMA), isSong);
      if (verdict.ok) {
        flags = [];
        break;
      }
      flags = verdict.problems.map(problem => `${PICTURE_FLAG_PREFIX} check: ${problem}`);
    } catch (err) {
      // The picture is already paid for: keep it and say it was not checked, rather than drawing again
      flags = [`${PICTURE_FLAG_PREFIX} was not checked: ${describe(err)}`];
      break;
    }
  }

  onStep('saving');
  const uploaded = await services.upload(image as Blob, `studio_${draft.id}.jpg`);
  return { imagePrompt, image: uploaded.filename, imageSize: uploaded.size, flags };
};
